import React, { useEffect, useState, useContext } from "react";
import PropTypes from "prop-types";
import { useMsal } from "@azure/msal-react";
import { Box, Typography, Divider, RadioGroup, FormControlLabel, FormControl } from "@mui/material";
import PrimaryRadio from "../../../../Buttons/PrimaryRadio/PrimaryRadio";
import SecondaryButton from "../../../../Buttons/SecondaryButton/SecondaryButton";
import PrimaryButton from "../../../../Buttons/PrimaryButton/PrimaryButton";
import UserAutocomplete from "../../../../Inputs/UserAutocomplete/UserAutocomplete";
import { SecurityService } from "../../../../../services/SecurityService";
import { CarrierService } from "../../../../../services/CarrierService";
import { AlertContext } from "../../../../Dashboard/Dashboard";
import { handleAltEnter } from "../../../../../utils/keyboardShortcutHelpers";

export default function CarrierNetworkManuallyAddStatus({
  networkUserId,
  cancel,
  networkStatus,
  getNetworkStatus,
  carrier,
  networkUser,
  getCarrier
}) {
  const { instance } = useMsal();
  const { warning } = useContext(AlertContext);
  const [userDefault, setUserDefault] = useState("");
  const [status, setStatus] = useState(networkStatus);
  const [submitDisabled, setSubmitDisabled] = useState(true);
  const [isUpdating, setIsUpdating] = useState(false);
  const [autocompleteDisabled, setAutocompleteDisabled] = useState(false);
  const [user, setUser] = useState("");

  const typeStatus = [
    {
      id: "OPEN",
      label: "Open"
    },
    {
      id: "LEAD",
      label: "Lead"
    },
    {
      id: "RELATN",
      label: "Relationship"
    }
  ];

  const HandleStatusChange = (event) => {
    setStatus(event.target.value);
  };

  const handleCancel = () => {
    setAutocompleteDisabled(false);
    setStatus(networkStatus);
    cancel(null);
  };

  const handleAddManually = () => {
    setIsUpdating(true);
    const payloadOpen = {
      carrierId: carrier.id,
    };

    switch (status) {
      case "OPEN":
        CarrierService.AddManualOpen(instance, payloadOpen).then(() => {
          setIsUpdating(false);
          getNetworkStatus();
          getCarrier();
        }).catch(error => {
          console.error(error);
          setIsUpdating(false);
          getNetworkStatus();
          warning(error);
        });
        break;
      case "LEAD":
      {
        const payload = {
          userId: user?.userIdMcld,
          carrierId: carrier.id,
        };
        CarrierService.AddManualLead(instance, payload).then(() => {
          setIsUpdating(false);
          getNetworkStatus();
          getCarrier();
        }).catch(error => {
          console.error(error);
          setIsUpdating(false);
          getNetworkStatus();
          warning(error);
        });
        break;
      }
      case "RELATN":
      {
        const payload = {
          userId: user?.userIdMcld,
          carrierId: carrier.id,
        };
        CarrierService.AddManualRelat(instance, payload).then(() => {
          setIsUpdating(false);
          getNetworkStatus();
          getCarrier();
        }).catch(error => {
          console.error(error);
          setIsUpdating(false);
          getNetworkStatus();
          warning(error);
        });
        break;
      }
      default:
        break;
    }
  };

  useEffect(() => {
    if (networkUserId === null) {
      setUserDefault("");
      setStatus(networkStatus);
      setAutocompleteDisabled(true);
    } else {
      SecurityService.GetUsers(instance).then(res => {
        setUserDefault(res.find(e => e.userIdMcld === networkUserId));
        setUser(res.find(e => e.userIdMcld === networkUserId));
        setStatus(networkStatus);
      }).catch(error => {
        console.error(error);
        warning(error);
      });
    }
  }, [networkUserId]);

  useEffect(() => {
    if (status !== "OPEN") {
      setSubmitDisabled(!user ? true : (user.userIdMcld === undefined || !status));
    } else if (status == "OPEN") {
      setSubmitDisabled(!status);
    }
  }, [user]);

  useEffect(() => {
    if (status === "OPEN") {
      setUser(userDefault);
      setAutocompleteDisabled(true);
      setSubmitDisabled(!status);
    } else if (status) {
      setAutocompleteDisabled(false);
      setSubmitDisabled(!user ? true : (user.userIdMcld === undefined || !status));
    }
  }, [status]);

  return (
    <Box sx={{ padding: "39px" }} onKeyDown={(e) => handleAltEnter(e, (submitDisabled || isUpdating), handleAddManually)}>
      <>
        <Typography variant="h2">
          Network Actions
        </Typography>
        <Divider sx={{ margin: "10px 0 20px 0", backgroundColor: "black_solid.50" }} />
        <Box sx={{ margin: "20px 0 25px 0" }}>
          <FormControl>
            <RadioGroup
              row
              aria-labelledby="carrier-search-radio-buttons"
              name="row-radio-buttons-group"
              value={status ?? networkStatus}
              onChange={HandleStatusChange}
            >
              {typeStatus.map((type, i) => (
                <FormControlLabel
                  sx={{ width: "78px" }}
                  key={`status-network-${i}`}
                  value={type.id}
                  control={<PrimaryRadio />}
                  label={<Typography variant="body2" color="black_solid.60">{type.label}</Typography>}
                />
              ))}
            </RadioGroup>
          </FormControl>
        </Box>
        <UserAutocomplete
          label="Select a Rep to assign this carrier to:"
          updateSelectedUser={setUser}
          error={status === "OPEN" ? false : !user}
          defaultInput={!userDefault ? "" : networkUser.userName === "undefined" ? "" : networkUser.userName}
          disabled={autocompleteDisabled}
          defaultSelected={!networkUser ? null : networkUser}
          openStatus={status === "OPEN"}
        />
        <Box sx={{ display: "flex", marginTop: "30px" }}>
          <Box sx={{ marginLeft: "auto", marginRight: "20px" }}>
            <SecondaryButton
              label="Cancel"
              onClick={handleCancel}
              disabled={isUpdating}
            />
          </Box>
          <PrimaryButton
            label="Go"
            width="40px"
            onClick={handleAddManually}
            isLoading={isUpdating}
            disabled={submitDisabled || isUpdating}
          />
        </Box>
      </>
    </Box>
  );
}

CarrierNetworkManuallyAddStatus.propTypes = {
  networkUserId: PropTypes.string,
  cancel: PropTypes.func,
  networkStatus: PropTypes.string,
  getNetworkStatus: PropTypes.func.isRequired,
  carrier: PropTypes.object.isRequired,
  networkUser: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.object
  ]),
  getCarrier: PropTypes.func.isRequired
};